import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    ArrowRight,
    Lock,
    CheckCircle,
    ShareNetwork,
    ClipboardText,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { Spinner, Button } from '@/components/ui';
import { FormFieldRenderer } from '@/components/features/FormFieldRenderer/FormFieldRenderer';
import { SectionProgress } from '@/components/features/SectionProgress/SectionProgress';
import { QuizScore } from '@/components/features/QuizScore/QuizScore';
import { useForm, useSubmitResponse } from '@/api';
import { useAuthStore } from '@/stores';
import { buildFormShareUrl, ROUTES } from '@/constants';

import type { Section, FormField, FormResponse } from '@/types/form';

import styles from './FormDetail.module.css';

interface SubmissionResult {
    response: FormResponse;
}

function evaluateConditionals(
    field: FormField,
    value: string,
): { action: 'go_to_section' | 'skip_to_end'; targetSectionId?: number } | null {
    if (!field.conditional_rules || field.conditional_rules.length === 0) return null;
    for (const rule of field.conditional_rules) {
        if (rule.value === value) {
            return {
                action: rule.action,
                targetSectionId: rule.target_section_id ?? undefined,
            };
        }
    }
    return null;
}

export function FormDetail() {
    const { formSlug } = useParams<{ userSlug: string; formSlug: string }>();
    const navigate = useNavigate();
    const { getUser } = useAuthStore();
    const user = getUser();

    const { data: form, isLoading, error } = useForm(formSlug || '');
    const submitResponse = useSubmitResponse(formSlug || '');

    const [values, setValues] = useState<Record<number, string>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
    const [submitted, setSubmitted] = useState(false);
    const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

    useEffect(() => {
        if (form) {
            document.title = `${form.title} — Ratio`;
        }
    }, [form]);

    const isOwner = form && user && form.author.id === user.id;

    // Build sections list — use form.sections if available, else wrap fields in single section
    const sections: Section[] = form
        ? form.sections?.length > 0
            ? form.sections
            : [{ id: 0, title: '', description: '', order: 0, fields: form.fields }]
        : [];

    const currentSection = sections[currentSectionIndex];
    const isLastSection = currentSectionIndex === sections.length - 1;
    const isFirstSection = currentSectionIndex === 0;

    const handleValueChange = useCallback((fieldId: number, val: string) => {
        setValues((prev) => ({ ...prev, [fieldId]: val }));
        if (fieldErrors[fieldId]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[fieldId];
                return next;
            });
        }
    }, [fieldErrors]);

    const validateSection = (section: Section): boolean => {
        const errors: Record<number, string> = {};
        for (const field of section.fields) {
            if (field.is_required) {
                const val = values[field.id] ?? '';
                if (!val.trim()) {
                    errors[field.id] = 'Este campo es obligatorio';
                }
            }
        }
        setFieldErrors((prev) => ({ ...prev, ...errors }));
        return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
        if (!currentSection) return;
        if (!validateSection(currentSection)) {
            toast.error('Completa todos los campos obligatorios');
            return;
        }

        // Evaluate conditional rules in current section fields
        let jumpToSectionId: number | null = null;
        let skipToEnd = false;

        for (const field of currentSection.fields) {
            const val = values[field.id] ?? '';
            const result = evaluateConditionals(field, val);
            if (result) {
                if (result.action === 'skip_to_end') {
                    skipToEnd = true;
                    break;
                }
                if (result.action === 'go_to_section' && result.targetSectionId != null) {
                    jumpToSectionId = result.targetSectionId;
                    break;
                }
            }
        }

        if (skipToEnd) {
            handleSubmit();
            return;
        }

        if (jumpToSectionId != null) {
            const targetIndex = sections.findIndex((s) => s.id === jumpToSectionId);
            if (targetIndex !== -1) {
                setCurrentSectionIndex(targetIndex);
                return;
            }
        }

        setCurrentSectionIndex((prev) => Math.min(prev + 1, sections.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePrev = () => {
        setCurrentSectionIndex((prev) => Math.max(prev - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = () => {
        if (!currentSection) return;
        if (!validateSection(currentSection)) {
            toast.error('Completa todos los campos obligatorios');
            return;
        }

        const allFields = sections.flatMap((s) => s.fields);
        const answers = allFields.map((field) => ({
            field_id: field.id,
            value: values[field.id] ?? '',
        }));

        submitResponse.mutate(
            { answers },
            {
                onSuccess: (response) => {
                    setSubmissionResult({ response });
                    setSubmitted(true);
                },
                onError: (err: unknown) => {
                    const apiErr = err as { response?: { data?: Record<string, string[]> }; message?: string };
                    const firstMsg = apiErr?.response?.data
                        ? Object.values(apiErr.response.data).flat()[0]
                        : null;
                    toast.error(firstMsg ?? 'No se pudo enviar la respuesta. Revisa tu conexion e intenta de nuevo.', { duration: 8000 });
                },
            }
        );
    };

    const handleShare = async () => {
        const url = window.location.href;
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Enlace copiado');
        } catch {
            toast.error('No se pudo copiar el enlace');
        }
    };

    if (isLoading) {
        return (
            <div className={styles.loading}>
                <Spinner size="lg" />
            </div>
        );
    }

    if (error || !form) {
        const status = (error as { response?: { status?: number } } | null)?.response?.status;
        if (status === 403) {
            return (
                <div className={styles.errorPage}>
                    <Lock size={48} className={styles.errorIcon} />
                    <h2 className={styles.errorTitle}>Acceso restringido</h2>
                    <p className={styles.errorText}>No tienes permiso para ver este formulario.</p>
                    <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                        Volver al inicio
                    </Button>
                </div>
            );
        }
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Formulario no encontrado</h2>
                <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                    Volver al inicio
                </Button>
            </div>
        );
    }

    // Success screen
    if (submitted) {
        const quizScore = submissionResult?.response?.quiz_score;
        const quizTotal = submissionResult?.response?.quiz_total;
        const responseId = submissionResult?.response?.id;
        const confirmMsg = form.confirmation_message || 'Respuesta registrada';

        return (
            <div className={styles.page}>
                <div className={styles.successScreen}>
                    <CheckCircle size={64} weight="fill" className={styles.successIcon} />
                    <h2 className={styles.successTitle}>{confirmMsg}</h2>
                    <p className={styles.successText}>Gracias por responder el formulario.</p>

                    {quizScore != null && quizTotal != null && (
                        <QuizScore score={quizScore} total={quizTotal} />
                    )}

                    <div className={styles.successActions}>
                        {form.allow_edit_response && responseId && (
                            <Button
                                variant="secondary"
                                onClick={() => navigate(
                                    `/${form.author.slug}/f/${form.slug}/respuestas/${responseId}`
                                )}
                            >
                                Ver mi respuesta
                            </Button>
                        )}
                        <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                            Volver al inicio
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Already responded
    if (form.user_has_responded && form.one_response_per_user && !isOwner) {
        return (
            <div className={styles.page}>
                <button className={styles.backBtn} onClick={() => navigate(ROUTES.feed)} aria-label="Volver">
                    <ArrowLeft size={18} />
                    Volver
                </button>
                <div className={styles.alreadyResponded}>
                    <CheckCircle size={40} weight="fill" className={styles.respondedIcon} />
                    <h2 className={styles.respondedTitle}>Ya respondiste</h2>
                    <p className={styles.respondedText}>Ya enviaste una respuesta a este formulario.</p>
                    {form.allow_edit_response && (
                        <p className={styles.respondedText}>
                            Puedes editar tu respuesta desde el historial.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const themeStyle = form.theme_color ? { borderTopColor: form.theme_color } : {};
    const hasSections = sections.length > 1;

    return (
        <div className={styles.page}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Volver">
                <ArrowLeft size={18} />
                Volver
            </button>

            <div className={styles.content}>
                {/* Header */}
                <div className={styles.formHeader} style={themeStyle}>
                    {form.theme_image_url && (
                        <img
                            src={form.theme_image_url}
                            alt={`Portada de ${form.title}`}
                            className={styles.themeImage}
                        />
                    )}
                    <div className={styles.formMeta}>
                        <span className={styles.formBadge}>
                            <ClipboardText size={12} weight="bold" />
                            {form.mode === 'quiz' ? 'Quiz' : 'Formulario'}
                        </span>
                        {form.is_closed && (
                            <span className={styles.closedBadge}>
                                <Lock size={12} weight="bold" />
                                Cerrado
                            </span>
                        )}
                    </div>
                    <h1 className={styles.formTitle}>{form.title}</h1>
                    {form.description && (
                        <p className={styles.formDescription}>{form.description}</p>
                    )}
                    <div className={styles.formStats}>
                        <span className={styles.formStat}>
                            por {form.author.display_name}
                        </span>
                        <span className={styles.formStatDot}>&middot;</span>
                        <span className={styles.formStat}>
                            {form.total_responses} {form.total_responses === 1 ? 'respuesta' : 'respuestas'}
                        </span>
                    </div>
                </div>

                {/* Owner actions */}
                {isOwner && (
                    <div className={styles.ownerActions}>
                        <button
                            type="button"
                            className={styles.ownerBtn}
                            onClick={() => navigate(`/${form.author.slug}/f/${form.slug}/respuestas`)}
                        >
                            Ver respuestas
                        </button>
                        <button
                            type="button"
                            className={styles.ownerBtn}
                            onClick={() => navigate(`/${form.author.slug}/f/${form.slug}/analiticas`)}
                        >
                            Analiticas
                        </button>
                        <button
                            type="button"
                            className={styles.ownerBtn}
                            onClick={() => navigate(buildFormShareUrl(form.author.slug, form.slug))}
                            aria-label="Compartir formulario"
                        >
                            <ShareNetwork size={14} />
                            Compartir
                        </button>
                    </div>
                )}

                {/* Closed banner */}
                {form.is_closed && (
                    <div className={styles.closedBanner}>
                        <Lock size={18} weight="bold" />
                        <span>Este formulario esta cerrado y ya no acepta respuestas</span>
                    </div>
                )}

                {/* Section progress bar */}
                {!form.is_closed && hasSections && form.show_progress_bar && (
                    <SectionProgress current={currentSectionIndex + 1} total={sections.length} />
                )}

                {/* Section title */}
                {!form.is_closed && hasSections && currentSection && (currentSection.title || currentSection.description) && (
                    <div className={styles.sectionInfo}>
                        {currentSection.title && (
                            <h2 className={styles.sectionTitle}>{currentSection.title}</h2>
                        )}
                        {currentSection.description && (
                            <p className={styles.sectionDesc}>{currentSection.description}</p>
                        )}
                    </div>
                )}

                {/* Fields */}
                {!form.is_closed && currentSection && (
                    <>
                        <div className={styles.fieldsList}>
                            {currentSection.fields.map((field) => (
                                <div key={field.id} className={styles.fieldCard}>
                                    <FormFieldRenderer
                                        field={field}
                                        value={values[field.id] ?? ''}
                                        setValue={(v) => handleValueChange(field.id, v)}
                                        error={fieldErrors[field.id]}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Navigation buttons */}
                        <div className={styles.navButtons}>
                            {hasSections && !isFirstSection && (
                                <button
                                    type="button"
                                    className={styles.prevBtn}
                                    onClick={handlePrev}
                                >
                                    <ArrowLeft size={16} />
                                    Anterior
                                </button>
                            )}

                            {hasSections && !isLastSection ? (
                                <button
                                    type="button"
                                    className={styles.nextBtn}
                                    onClick={handleNext}
                                >
                                    Siguiente
                                    <ArrowRight size={16} />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.submitBtn}
                                    onClick={handleSubmit}
                                    disabled={submitResponse.isPending}
                                >
                                    <span style={{ visibility: submitResponse.isPending ? 'hidden' : 'visible' }}>Enviar respuesta</span>
                                    {submitResponse.isPending && <Spinner size="sm" variant="inverse" className={styles.btnSpinner} />}
                                </button>
                            )}
                        </div>

                        <p className={styles.requiredNote}>* Campos obligatorios</p>
                    </>
                )}

                {form.is_closed && isOwner && (
                    <button
                        type="button"
                        className={styles.ownerBtn}
                        onClick={handleShare}
                        aria-label="Compartir"
                    >
                        <ShareNetwork size={14} />
                        Compartir enlace
                    </button>
                )}
            </div>
        </div>
    );
}
